
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../home/components/Footer';
import { fetchProperties, PublicProperty } from '../../../lib/api';

interface Property {
  id: string;
  title: string;
  location: string;
  price: string;
  image: string;
  rating: number;
  reviews: number;
  features: string[];
  category: string;
  description: string;
  bedrooms?: number;
  bathrooms?: number;
  area?: string;
}

export default function Properties() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [propertiesBySlug, setPropertiesBySlug] = useState<Record<string, PublicProperty[]>>({});

  const categories = [
    { id: 'all', name: 'All Properties', icon: 'ri-home-line' },
    { id: 'boats', name: 'Boats', icon: 'ri-ship-line' },
    { id: 'apartments', name: 'Apartments', icon: 'ri-building-line' },
    { id: 'buildings', name: 'Buildings', icon: 'ri-building-2-line' },
    { id: 'beach-houses', name: 'Beach Houses', icon: 'ri-home-smile-line' },
    { id: 'furniture', name: 'Furniture', icon: 'ri-sofa-line' },
    { id: 'cars', name: 'Cars', icon: 'ri-car-line' },
  ];

  const categoryToSlug = (cat?: PublicProperty['category']): string | null => {
    switch (cat) {
      case 'Boat':
        return 'boats';
      case 'Apartment':
        return 'apartments';
      case 'Building & Selling':
        return 'buildings';
      case 'Beach House':
        return 'beach-houses';
      case 'Furniture':
        return 'furniture';
      case 'Car':
        return 'cars';
      default:
        return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const list = await fetchProperties();
        const organized: Record<string, PublicProperty[]> = {};
        for (const p of list) {
          const slug = categoryToSlug(p.category);
          if (!slug) continue;
          if (!organized[slug]) organized[slug] = [];
          organized[slug].push(p);
        }
        setPropertiesBySlug(organized);
      } catch (e: any) {
        setError(e.message || 'Failed to fetch properties');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatPrice = (p: PublicProperty): string => {
    const value = typeof p.display_price === 'number' ? p.display_price : 0;
    const label = p.display_price_label ? ` / ${p.display_price_label}` : '';
    return value ? `${value.toLocaleString()}${label}` : '';
  };

  const computedFeatures = (p: PublicProperty): string[] => {
    return [
      p.bedrooms ? `${p.bedrooms} Beds` : undefined,
      p.bathrooms ? `${p.bathrooms} Baths` : undefined,
      p.area_sqft ? `${p.area_sqft} sqft` : undefined,
      p.category ? `${p.category}` : undefined,
    ].filter(Boolean) as string[];
  };

  const filteredProperties: PublicProperty[] = (() => {
    if (activeCategory === 'all') {
      return Object.values(propertiesBySlug).flat();
    }
    return propertiesBySlug[activeCategory] || [];
  })();

  const getSlugForProperty = (p: PublicProperty): string => {
    return categoryToSlug(p.category) || 'all';
  };

  const handlePropertyClick = (p: PublicProperty) => {
    const slug = activeCategory === 'all' ? getSlugForProperty(p) : activeCategory;
    // Pass selected property via navigation state to avoid details refetch issues
    navigate(`/property/${slug}/${p.id}`,
      { state: { property: p } }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <section className="py-20 text-center">Loading properties...</section>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <section className="py-20 text-center text-red-500">Error: {error}</section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

  {/* Hero Section */}
  <section className="pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-12 md:pb-16 bg-gradient-to-br from-blue-600 to-teal-600">
    <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
      <div className="text-center text-white">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          Our Properties
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl opacity-90 max-w-3xl mx-auto px-2">
          Discover premium real estate, luxury boats, and exquisite collections across Nigeria
        </p>
      </div>
    </div>
  </section>

  {/* Category Filter */}
  <section className="py-6 sm:py-8 md:py-12 bg-white shadow-sm">
    <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
      <div className="flex overflow-x-auto scrollbar-hide pb-2 sm:pb-0">
        <div className="flex space-x-2 sm:space-x-3 md:space-x-4 min-w-max sm:min-w-0 sm:justify-center sm:w-full px-2 sm:px-0">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center space-x-1.5 sm:space-x-2 px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-full text-sm sm:text-base md:text-lg font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              <i className={`${category.icon} text-base sm:text-lg md:text-xl`}></i>
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  </section>

  {/* Properties Grid */}
  <section className="py-8 sm:py-12 md:py-16 lg:py-20">
    <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
        {filteredProperties.map((property) => (
          <div
            key={property.id}
            onClick={() => handlePropertyClick(property)}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105 overflow-hidden group"
            data-product-shop
          >
            <div className="relative overflow-hidden">
              <img
                src={property.image_url}
                alt={property.title}
                className="w-full h-48 sm:h-56 md:h-64 lg:h-72 object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-white/90 backdrop-blur-sm rounded-full px-2 sm:px-3 py-1 sm:py-1.5">
                <div className="flex items-center space-x-1">
                  <i className="ri-star-fill text-yellow-400 text-xs sm:text-sm"></i>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">{property.rating}</span>
                </div>
              </div>
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 bg-blue-600/90 backdrop-blur-sm text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                <span className="text-xs sm:text-sm font-medium capitalize">{getSlugForProperty(property).replace('-', ' ')}</span>
              </div>
            </div>
            
            <div className="p-4 sm:p-5 md:p-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2">{property.title}</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-3 sm:mb-4 flex items-center">
                <i className="ri-map-pin-line mr-1 sm:mr-2 text-blue-600 flex-shrink-0"></i>
                <span className="line-clamp-1">{property.location}</span>
              </p>
              
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">{formatPrice(property)}</p>
                <p className="text-xs sm:text-sm text-gray-500">({property.total_reviews} reviews)</p>
              </div>

              {/* Property Details */}
              {(property.bedrooms || property.bathrooms || property.area_sqft) && (
                <div className="flex items-center space-x-4 sm:space-x-6 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600">
                  {property.bedrooms && (
                    <div className="flex items-center">
                      <i className="ri-hotel-bed-line mr-1 text-blue-600"></i>
                      <span>{property.bedrooms} Beds</span>
                    </div>
                  )}
                  {property.bathrooms && (
                    <div className="flex items-center">
                      <i className="ri-drop-line mr-1 text-blue-600"></i>
                      <span>{property.bathrooms} Baths</span>
                    </div>
                  )}
                  {property.area_sqft && (
                    <div className="flex items-center">
                      <i className="ri-ruler-line mr-1 text-blue-600"></i>
                      <span>{property.area_sqft} sqft</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-4 sm:mb-6">
                {computedFeatures(property).slice(0, 4).map((feature, index) => (
                  <div key={index} className="flex items-center text-xs sm:text-sm text-gray-600">
                    <i className="ri-check-line text-green-500 mr-1 sm:mr-1.5 text-xs sm:text-sm flex-shrink-0"></i>
                    <span className="truncate">{feature}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-4">
                {property.description || 'No description available.'}
              </p>

              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-300 whitespace-nowrap">
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredProperties.length === 0 && (
        <div className="text-center py-12 sm:py-16 md:py-20">
          <i className="ri-search-line text-4xl sm:text-5xl md:text-6xl text-gray-400 mb-4 sm:mb-6"></i>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">No property listed</h3>
        </div>
      )}
    </div>
  </section>

  <Footer />
</div>
  );
}
